import _ from "underscore";
import rison from "rison";
import DriveView from "../../../DriveView";
import { Device } from "../../../model/Device";
import { dstCalculator } from "../../../utils/date";
import DVIRLog from "../../../model/DVIRLog";
import { TrailerAttachments } from "../../../model/TrailerAttachments";
import { dispatcher, DispatcherEvents } from "../../../utils/dispatcher";
import { BaseCache, makeDeviceCache } from "../../../utils/storage/cache";
import { getCurrentVehicle } from "../../../utils/device";
import dayjs from "../../../utils/dayjs";
import { InspectionType } from "../inspectionHelper";
import { PageLevel } from "../../../store/mobile/types";
import { getCurrentLocationUsingDeviceLocationAndGps } from "../../../utils/location";
import { handleApi } from "../../../utils/error";
import { populateAddressesRedux } from "../../../sync/common";
import {
    isFromProvince,
    Province,
    isDeviceFromOntario
} from "../../../modules/inspections/utils/regionUtils";
import { Trailer } from "../../../model/Trailer";
import { CancelablePromise as Promise } from "../../../utils/cancelablePromise";
import pageHTML from "../../dvir/shared/main.tmpl";
import logTemplate from "./dvirReportLog.tmpl";
import { resolveLastRegulatoryInspection } from "../../../modules/hos/roadsideCheck/helpers/lastInspectionRedirect";
import { getInspectionLogTime } from "../../../modules/inspections/utils/assetFormatters";
import { INSPECTION_HISTORY_DETAILS_HASH } from "../../../utils/uri";
import { InspectionActions } from "../../../modules/inspections/store/actions";
import { IInspectionAsset } from "../../../modules/inspections/store/types";
import { RoadsideCheckLanguageSelector } from "../roadsideCheckLanguageSelector";

export default class DvirReportLogsView extends DriveView<Backbone.Model> {
    private readonly template = _.template(pageHTML);
    private readonly logTemplate = _.template(logTemplate);
    private readonly trailerAttachmentCollection: TrailerAttachments;
    private readonly deviceCache: BaseCache<Device | IDevice>;
    private readonly currentVehicle: IIdEntity;
    private dvirCollection: DVIRLog[];
    private isOntario?: boolean;
    private language: string;
    private isReviewPending = false;
    private isDestroyed = false;
    private regulatoryAssetPromise: globalThis.Promise<IInspectionAsset | null>;
    private regulatoryAsset: IInspectionAsset | null = null;
    private readonly languageSelector = new RoadsideCheckLanguageSelector({
        app: this.app,
        getTranslator: () => this.translator,
        getElement: () => this.el,
        getHosRuleSetIdShortened: () => this.app.hosUtils.getLatestHosRuleSetIdShortened(),
        onLanguageApplied: () => {
            // No explicit re-render here: the store dispatch in applyLanguage() already triggers
            // InspectionView's store listener -> switchLanguage() -> render() synchronously
            // (Redux dispatch/subscribe is synchronous), so calling render() again here would
            // just be a redundant duplicate render.
        }
    });

    constructor(options) {
        super(options, {
            title: "Roadside Check",
            activeSection: "inspection",
            pageLevel: PageLevel.RETURN_DASHBOARD
        });
        this.app = options.app;
        this.deviceCache = makeDeviceCache(this.app.store);
        this.trailerAttachmentCollection = new TrailerAttachments(
            this.app.api,
            this.app.store
        );
        this.listenTo(dispatcher, DispatcherEvents.SyncDvir, () => {
            this.dvirCollection = this.app.dvirLogsService.getDvirLogs();
            this.trailerAttachmentCollection.fetch();
            // A sync can change which record is the vehicle's last completed inspection,
            // so redo the regulatory-vs-DVIR comparison before re-rendering.
            this.regulatoryAssetPromise = this.resolveRegulatoryAsset();
            this.render();
        });

        this.currentVehicle = getCurrentVehicle() as IIdEntity;
        this.dvirCollection = this.app.dvirLogsService.getDvirLogs();
        this.language = this.app.store.getState().mobile.language || "en";

        // Resolve the current vehicle's last regulatory inspection up front so the vehicle
        // row can offer "View" even when no legacy DVIR exists (MYG-141213).
        this.regulatoryAssetPromise = this.resolveRegulatoryAsset();
    }

    remove() {
        // Native promises can't be cancelled; the flag stops pending callbacks from
        // re-rendering a view that has been detached from the DOM.
        this.isDestroyed = true;
        this.languageSelector.remove();
        return super.remove();
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click .review-dvir": this.review,
            "click #roadside-check-language-selector": this.onLanguageSelectorClick
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    onLanguageSelectorClick() {
        this.languageSelector.handleClick();
    }

    render() {
        if (!this.currentVehicle || this.currentVehicle.id === "NoDeviceId") {
            this.app.router.navigate("main", true);
            return this;
        }

        let currentDeviceFromCache = this.deviceCache.get({
            id: this.currentVehicle.id
        })[0] as IDevice;
        let vehicleLogsTemplate;
        let trailerLogsTemplate;

        const languageViewModel = this.languageSelector.getViewModel();

        let formatLogDate = (dateTime) => dayjs(dstCalculator.toLocalDateTime(dateTime))
            .locale(this.language)
            .format("lll");

        let getLogTemplate = (log) => {
            return this.logTemplate({
                log: log,
                logDateString: formatLogDate(log.dateTime),
                hasLastLog: !!log.id
            });
        };

        let lastVehicleLog = this.app.dvirLogsService.getLatestLog(
            this.dvirCollection,
            this.currentVehicle.id,
            false
        );
        let trailers: Trailer[];
        let latestTrailerDvirs: (DVIRLog | null)[];

        let isOntarioInTripPromise = lastVehicleLog?.isInTrip()
            ? this.isOntarioInTripDvir(currentDeviceFromCache)
            : Promise.resolve(false);
        isOntarioInTripPromise
            .then((isOntarioInTrip) => {
                if (isOntarioInTrip) {
                    let prePostTripLog = this.app.dvirLogsService.getLatestLog(
                        this.dvirCollection,
                        this.currentVehicle.id,
                        true
                    );
                    lastVehicleLog = prePostTripLog || lastVehicleLog;
                }

                let currentDevice =
                    currentDeviceFromCache &&
                    new Device(currentDeviceFromCache);
                if (lastVehicleLog && currentDevice) {
                    lastVehicleLog.device = currentDevice;
                }
                // When the vehicle has no legacy DVIR but does have a regulatory inspection on
                // the new Inspection page, enable the row anyway so the driver can present it
                // during a roadside check (MYG-141213). review() redirects via the empty data-id.
                // Built directly (not via getLogTemplate) because the date and enabled state
                // come from the regulatory asset, not the placeholder DVIRLog.
                const regulatoryTime = !lastVehicleLog && this.regulatoryAsset?.inspectionLog
                    ? getInspectionLogTime(this.regulatoryAsset.inspectionLog)
                    : 0;
                if (regulatoryTime) {
                    vehicleLogsTemplate = this.logTemplate({
                        log: new DVIRLog({
                            id: "",
                            device: currentDevice || this.currentVehicle
                        }),
                        logDateString: formatLogDate(new Date(regulatoryTime).toISOString()),
                        hasLastLog: true
                    });
                } else {
                    vehicleLogsTemplate = getLogTemplate(
                        lastVehicleLog ||
                            new DVIRLog({
                                id: "",
                                device: currentDevice || this.currentVehicle
                            })
                    );
                }

                trailers =
                    this.trailerAttachmentCollection.getCurrentTrailers();
                trailers.sort((a, b) => a.name.localeCompare(b.name));
                latestTrailerDvirs = trailers.map((trailer) =>
                    this.app.dvirLogsService.getLatestLog(
                        this.dvirCollection,
                        trailer.id,
                        false
                    ));

                return latestTrailerDvirs.some((trailerDvirs) =>
                    trailerDvirs?.isInTrip())
                    ? this.isOntarioInTripDvir(currentDeviceFromCache)
                    : Promise.resolve(false);
            })
            .then((isOntarioInTrip) => {
                trailerLogsTemplate = trailers.map((trailer, index) => {
                    let lastTrailerLog = latestTrailerDvirs[index];
                    if (lastTrailerLog?.isInTrip() && isOntarioInTrip) {
                        let prePostTripLog =
                            this.app.dvirLogsService.getLatestLog(
                                this.dvirCollection,
                                trailer.id,
                                true
                            );
                        lastTrailerLog = prePostTripLog || lastTrailerLog;
                    }
                    if (lastTrailerLog) {
                        lastTrailerLog.trailer = trailer;
                    }
                    return getLogTemplate(
                        lastTrailerLog ||
                            new DVIRLog({
                                id: "",
                                trailer: trailer
                            })
                    );
                });

                return this.display(
                    this.template({
                        vehicleLogsTemplate,
                        trailerLogsTemplate,
                        ...languageViewModel
                    }),
                    this.language
                );
            })
            .catch((e) => {
                e && handleApi(e);
            });

        // Re-render once the regulatory lookup resolves when it should enable the vehicle row
        // (no legacy DVIR present). Later renders read the cached asset synchronously, and the
        // isNewlyResolved guard prevents this from looping.
        void this.regulatoryAssetPromise.then((asset) => {
            if (this.isDestroyed) {
                return;
            }
            const isNewlyResolved = !!asset && !this.regulatoryAsset;
            this.regulatoryAsset = asset;
            if (isNewlyResolved && !lastVehicleLog) {
                this.render();
            }
        });
        return this;
    }

    switchLanguage(language): void {
        if (language !== this.language) {
            this.language = language;
            this.render();
        }
    }

    /** Click handler for the report rows' "View" buttons (bound in events()). */
    review(e): void {
        if (this.isReviewPending) {
            return;
        }
        const id = e.currentTarget.attributes.getNamedItem("data-id").value;
        const resourceId = e.currentTarget.attributes.getNamedItem("data-resource-id")?.value;

        // The regulatory redirect is scoped to the current vehicle (MYG-141213): trailer rows
        // always open their legacy DVIR report directly.
        if (resourceId !== this.currentVehicle?.id) {
            this.navigateToLegacyReport(id);
            return;
        }

        this.isReviewPending = true;
        // Promise.prototype.finally is not in this project's TS lib (ES2015), so pass the
        // cleanup as both fulfillment and rejection handler — equivalent semantics: the
        // flag is cleared even if the catch handler itself throws.
        const clearReviewPending = () => {
            this.isReviewPending = false;
        };

        // Await the shared lookup (started in the constructor, restarted on every DVIR sync)
        // instead of re-querying: a tap during a DOT stop must not pay an extra round-trip,
        // and the promise already reflects the latest synced data.
        this.regulatoryAssetPromise
            .then((regulatoryAsset) => {
                if (regulatoryAsset) {
                    this.app.store.dispatch(InspectionActions.setSelectedHistoryAsset(regulatoryAsset));
                    this.app.router.navigate(INSPECTION_HISTORY_DETAILS_HASH, true);
                    return;
                }

                if (id) {
                    this.navigateToLegacyReport(id);
                }
            })
            .catch((error) => {
                error && handleApi(error);
            })
            .then(clearReviewPending, clearReviewPending);
    }

    /**
     * Starts (or restarts, after a DVIR sync) the lookup of the vehicle's last regulatory
     * inspection. render() and review() share this promise instead of re-querying.
     */
    private resolveRegulatoryAsset(): globalThis.Promise<IInspectionAsset | null> {
        return resolveLastRegulatoryInspection(this.app, this.app.api, this.app.store);
    }

    private isOntarioInTripDvir(device?: IDevice): Promise<boolean> {
        return new Promise((resolve) => {
            if (this.isOntario !== undefined) {
                resolve(this.isOntario);
                return;
            }

            if (device && device.licenseState) {
                this.isOntario = isDeviceFromOntario(device.licenseState);
                resolve(this.isOntario);
                return;
            }

            return getCurrentLocationUsingDeviceLocationAndGps(
                this.app.api,
                this.app.store
            )
                .then((location) => {
                    if (!location || !location.location) {
                        resolve(false);
                        return;
                    }
                    return populateAddressesRedux(
                        this.app.api,
                        this.app.store,
                        [{ id: "", coordinate: location.location }],
                        true
                    ).then((addresses) => {
                        let formattedAddress = addresses && addresses[0];
                        let region: string | null =
                            (formattedAddress && formattedAddress.region) ||
                            null;
                        let country: string | null =
                            (formattedAddress && formattedAddress.country) ||
                            null;
                        if (region && country) {
                            this.isOntario = isFromProvince(
                                [Province.Ontario],
                                region
                            );
                            resolve(this.isOntario);
                            return;
                        }
                        resolve(false);
                        return;
                    });
                })
                .catch((e) => {
                    e && handleApi(e);
                    resolve(false);
                    return;
                });
        });
    }

    private navigateToLegacyReport(id: string) {
        this.app.router.navigate(
            `inspection/main,${ rison.encode({ type: InspectionType.DVIR_REPORT, id, isOntario: this.isOntario || false }) }`,
            true
        );
    }
}
